import React, { useCallback } from 'react';
import { PlayerButton } from './PlayerButton';

/** Release focus after slider interaction so keyboard shortcuts keep working. */
const blurTarget = (e: React.SyntheticEvent) => (e.target as HTMLElement).blur();

interface VolumeControlProps {
  volume: number;
  isMuted: boolean;
  mediaElementId: string;
  onVolumeChange: (volume: number) => void;
  onMuteToggle: () => void;
}

export const VolumeControl = React.memo(function VolumeControl({
  volume,
  isMuted,
  mediaElementId,
  onVolumeChange,
  onMuteToggle,
}: VolumeControlProps) {
  const displayVolume = isMuted ? 0 : volume;

  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onVolumeChange(parseFloat(e.target.value));
    },
    [onVolumeChange],
  );

  const muteLabel = isMuted || volume === 0 ? 'Unmute' : 'Mute';

  return (
    <div className="kollab-player-volume">
      <PlayerButton
        className={`kollab-player-mute-btn ${isMuted || volume === 0 ? 'kollab-player-mute-btn--muted' : ''}`}
        tooltip={muteLabel}
        aria-controls={mediaElementId}
        onClick={onMuteToggle}
      />
      <input
        type="range"
        className="kollab-player-volume-slider"
        min="0"
        max="1"
        step="0.05"
        value={displayVolume}
        onChange={handleSliderChange}
        onMouseUp={blurTarget}
        onTouchEnd={blurTarget as unknown as React.TouchEventHandler<HTMLInputElement>}
        aria-label="Volume"
        aria-controls={mediaElementId}
        aria-valuenow={Math.round(displayVolume * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={`${Math.round(displayVolume * 100)} percent`}
      />
    </div>
  );
});
